import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import ReassuranceBar from '@/components/feature/ReassuranceBar';
import CTABanner from '@/components/feature/CTABanner';
import SEOHead from '@/components/feature/SEOHead';
import { useState } from 'react';

const faqData = [
  {
    q: "Qu'est-ce que le survitrage et à quoi ça sert ?",
    a: "Le survitrage consiste à ajouter une seconde vitre sur une fenêtre simple vitrage existante, sans retirer le vitrage d'origine. Cela crée une lame d'air isolante qui améliore significativement les performances thermiques et acoustiques de la fenêtre, sans avoir à la changer entièrement.",
  },
  {
    q: "Le survitrage est-il efficace par rapport à un double vitrage ?",
    a: "Le survitrage offre des performances thermiques et acoustiques proches de celles d'un double vitrage standard. Il ne remplace pas un double vitrage VIR haut de gamme, mais il constitue une excellente solution économique quand le remplacement complet de la fenêtre n'est pas envisageable (copropriété, bâtiment classé, budget limité).",
  },
  {
    q: "Le survitrage se voit-il depuis l'extérieur ?",
    a: "Il existe des solutions de survitrage discret, notamment en aluminium laqué assorti à la menuiserie existante. Le survitrage peut être fixe ou ouvrant. Son intégration visuelle est étudiée au cas par cas pour respecter l'esthétique de votre façade.",
  },
];

export default function Survitrage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const serviceJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Survitrage à Lyon : isolation des fenêtres',
    provider: { '@type': 'HomeAndConstructionBusiness', name: 'DFPRO Lyon', telephone: '+33980803684' },
    areaServed: [{ '@type': 'City', name: 'Lyon' }, { '@type': 'AdministrativeArea', name: 'Métropole de Lyon' }],
    description: 'Survitrage à Lyon : solution d\'isolation sans changer la fenêtre. Amélioration thermique et acoustique. Devis gratuit, garantie 10 ans.',
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((item) => ({ '@type': 'Question', name: item.q, acceptedAnswer: { '@type': 'Answer', text: item.a } })),
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Accueil', item: 'https://depanfenetres-lyon.fr/' },
      { '@type': 'ListItem', position: 2, name: 'Vitrier à Lyon', item: 'https://depanfenetres-lyon.fr/vitrier-lyon/' },
      { '@type': 'ListItem', position: 3, name: 'Survitrage', item: 'https://depanfenetres-lyon.fr/survitrage-lyon/' },
    ],
  };

  return (
    <>
      <SEOHead
        title="Survitrage à Lyon : isolation des fenêtres | DFPRO"
        description="Survitrage à Lyon : solution d'isolation sans changer la fenêtre. Améliorez votre confort thermique et acoustique. Devis gratuit, garantie 10 ans. DFPRO Lyon."
        canonicalUrl="https://depanfenetres-lyon.fr/survitrage-lyon/"
        ogTitle="Survitrage à Lyon — Isolez vos fenêtres sans les changer | DFPRO"
        ogDescription="Survitrage pour fenêtre simple vitrage à Lyon. Économique, performant, discret. Devis gratuit."
        jsonLd={{ ...serviceJsonLd, ...faqJsonLd, ...breadcrumbJsonLd }}
      />

      <Header />

      <main>
        <div className="pt-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
            <nav className="flex items-center gap-2 text-xs text-secondary-400">
              <a href="/" className="hover:text-primary-500 transition-colors">Accueil</a>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
              <a href="/vitrier-lyon/" className="hover:text-primary-500 transition-colors">Vitrier à Lyon</a>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
              <span className="text-foreground-700 font-medium">Survitrage</span>
            </nav>
          </div>
        </div>

        <section className="relative w-full min-h-[380px] md:min-h-[420px] flex items-center bg-background-50">
          <div className="absolute inset-0">
            <img
              src="/images/survitrage-hero-2026.jpg"
              alt="Survitrage à Lyon — isolation thermique sans changer la fenêtre par DFPRO Lyon"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-foreground-900/50 via-foreground-900/35 to-foreground-900/50"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 py-14 md:py-16">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="font-heading font-bold text-2xl md:text-4xl text-background-50 leading-tight">
                Survitrage à Lyon
              </h1>
              <p className="mt-4 md:mt-5 text-sm md:text-lg text-background-50/90 max-w-2xl mx-auto leading-relaxed">
                Vous souhaitez améliorer l'isolation de vos fenêtres sans les remplacer ? Le survitrage est la solution économique et performante pour gagner en confort à Lyon.
              </p>
              <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
                <a href="tel:+33980803684" className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-background-50 font-semibold text-sm md:text-base px-6 py-3.5 rounded-md transition-colors whitespace-nowrap cursor-pointer shadow-[0_2px_8px_rgba(242,140,40,0.4)]">
                  <i className="ri-phone-fill text-lg w-5 h-5 flex items-center justify-center"></i>
                  Appeler — 09 80 80 36 84
                </a>
              </div>
            </div>
          </div>
        </section>

        <ReassuranceBar />

        <section className="w-full bg-background-50 py-14 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-12">
              <div className="lg:col-span-2">
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-800 mb-5">
                  Le survitrage à Lyon : isolez vos fenêtres sans tout changer
                </h2>
                <div className="space-y-4 text-sm md:text-base text-secondary-500 leading-relaxed">
                  <p>
                    Le survitrage est une solution ingénieuse pour améliorer l'isolation thermique et acoustique de fenêtres existantes, <strong className="text-foreground-800">sans avoir à remplacer la menuiserie</strong>. Il consiste à ajouter une seconde vitre (fixe ou ouvrante) sur le côté intérieur d'une fenêtre déjà en place, créant ainsi une lame d'air isolante entre le vitrage d'origine et le nouveau vitrage.
                  </p>
                  <p>
                    Cette solution est particulièrement adaptée à plusieurs situations que nous rencontrons fréquemment à Lyon : <strong className="text-foreground-800">copropriétés</strong> où le changement des fenêtres nécessite une autorisation en assemblée générale, <strong className="text-foreground-800">bâtiments anciens ou classés</strong> où la modification des menuiseries extérieures est interdite, ou tout simplement <strong className="text-foreground-800">budgets serrés</strong> pour lesquels le survitrage représente une alternative deux à trois fois moins chère que le remplacement complet des fenêtres.
                  </p>
                  <p>
                    Les bénéfices du survitrage sont immédiats : <strong className="text-foreground-800">réduction des déperditions thermiques</strong> (jusqu'à 50% par rapport à un simple vitrage), atténuation sensible des bruits extérieurs, et <strong className="text-foreground-800">suppression des sensations de paroi froide</strong> près des fenêtres. En hiver, votre logement conserve mieux la chaleur ; en été, le survitrage contribue aussi à limiter les entrées de chaleur.
                  </p>
                  <p>
                    Nous proposons deux types de survitrage à Lyon : le <strong className="text-foreground-800">survitrage fixe</strong>, monté de façon permanente, idéal pour les fenêtres qu'on n'ouvre jamais ; et le <strong className="text-foreground-800">survitrage ouvrant</strong>, monté sur charnières ou sur coulisses, qui permet de continuer à ouvrir la fenêtre pour aérer. Dans les deux cas, le profilé est en aluminium laqué, disponible en plusieurs teintes pour s'harmoniser avec votre décoration intérieure.
                  </p>
                  <p>
                    La pose d'un survitrage est rapide et ne génère pas de gros travaux : pas de dépose de la fenêtre existante, pas de maçonnerie, pas de finition à repeindre. Nos vitriers interviennent à Lyon et dans la métropole (Villeurbanne, Vénissieux, Bron, Vaulx-en-Velin, etc.) pour une pose en une demi-journée environ selon le nombre de fenêtres. Chaque installation bénéficie de notre <strong className="text-foreground-800">garantie 10 ans</strong>.
                  </p>
                </div>
              </div>

              <div className="bg-background-100 rounded-lg p-5 md:p-6 h-fit">
                <h3 className="font-heading font-semibold text-base text-foreground-800 mb-4">Nos services vitrier</h3>
                <nav className="space-y-2">
                  {[
                    { label: 'Vitrier à Lyon (pilier)', href: '/vitrier-lyon/' },
                    { label: 'Remplacement vitre cassée', href: '/remplacement-vitre-cassee-lyon/' },
                    { label: 'Remplacement double vitrage', href: '/remplacement-double-vitrage-lyon/' },
                    { label: 'Double vitrage embué', href: '/double-vitrage-embue-lyon/' },
                    { label: 'Vitrier urgence', href: '/vitrier-urgence-lyon/' },
                  ].map((link, idx) => (
                    <a key={idx} href={link.href} className="flex items-center gap-2 text-sm text-secondary-500 hover:text-primary-500 transition-colors py-1.5 cursor-pointer">
                      <i className="ri-arrow-right-s-line text-primary-400 w-4 h-4 flex items-center justify-center"></i>
                      {link.label}
                    </a>
                  ))}
                </nav>
                <div className="mt-5 pt-5 border-t border-background-200">
                  <a href="tel:+33980803684" className="inline-flex items-center justify-center gap-2 w-full bg-accent-500 hover:bg-accent-600 text-background-50 font-semibold text-sm py-3 rounded-md transition-colors cursor-pointer">
                    <i className="ri-phone-fill w-4 h-4 flex items-center justify-center"></i>
                    Appeler — 09 80 80 36 84
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 py-14 md:py-20">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="font-heading font-bold text-xl md:text-3xl text-foreground-800">Questions fréquentes</h2>
            </div>
            <div className="space-y-3">
              {faqData.map((item, idx) => (
                <div key={idx} className="bg-background-50 rounded-lg border border-background-200 overflow-hidden">
                  <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer">
                    <span className="font-heading font-semibold text-sm md:text-base text-foreground-800 pr-4">{item.q}</span>
                    <span className={`flex-shrink-0 transition-transform duration-200 ${openFaq === idx ? 'rotate-180' : ''}`}>
                      <i className="ri-arrow-down-s-line text-secondary-400 w-5 h-5 flex items-center justify-center"></i>
                    </span>
                  </button>
                  {openFaq === idx && (
                    <div className="px-5 pb-4"><p className="text-sm text-secondary-500 leading-relaxed">{item.a}</p></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        <CTABanner
          title="Un projet de survitrage à Lyon ?"
          subtitle="Appelez le 09 80 80 36 84 pour un devis gratuit. Solution économique, isolation performante."
          buttonText="Appeler maintenant"
          secondaryButtonText="Demander un devis"
          secondaryButtonHref="/contact/"
        />
      </main>
      <Footer />
    </>
  );
}